/**
 * Карточка серии поверх «Чатов» — как `#/info?serial=` CRM (`ProductCard.vue`): место и RFID,
 * «Сбор» (что записано в CRM и что должно быть по правилам кронов — расхождение красным),
 * брони с пересечениями и повторкой сделки, повторки с этой серией, задачи и комментарии, цена.
 * Открывается кликом по серии товара во вкладке «Договор»; в окне — поле «другая серия».
 * Только чтение. Спека `docs/specs/2026-10-22-serial-card-design.md`.
 */
import { useQuery } from "@tanstack/react-query";
import { ExternalLink, MapPin, RefreshCw, Smartphone, TriangleAlert, Wifi, X } from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";

import { TrackLine } from "@/components/chat/DealTracks";
import { SerialModels } from "@/components/chat/SerialModels";
import type { OnOffer } from "@/components/chat/SerialPhotos";
import registry from "@/nav/links.json";
import { type Track, loadSerialTracks, serialTracksKey } from "@/lib/dealTracks";
import { type SborValue, type SerialCard as Card, type SerialReserve, serialCardQuery } from "@/lib/serialCard";

const CRM = (registry.systems as Record<string, string>).crm;
const pill = "rounded-full px-1.5 py-px text-[11px] font-medium leading-[16px]";
const block = "flex flex-col gap-1.5 border-t border-border py-3";
const head = "text-[11.5px] font-semibold uppercase tracking-wide text-fg-muted";
const rub = (n: number | null) => (n == null ? "—" : `${n.toLocaleString("ru-RU")} ₽`);
const year = String(new Date().getFullYear());

/** `DD.MM`, другой год — `DD.MM.YY`; с временем — ` ЧЧ:ММ`, кроме полуночи. */
function when(iso: string | null | undefined, time = true) {
  if (!iso) return "—";
  const d = `${iso.slice(8, 10)}.${iso.slice(5, 7)}${iso.slice(0, 4) === year ? "" : `.${iso.slice(2, 4)}`}`;
  const t = iso.slice(11, 16);
  return time && t && t !== "00:00" ? `${d} ${t}` : d;
}

function Deal({ id, base }: { id: number | null; base: string }) {
  if (!id) return <span className="text-fg-faint">—</span>;
  return (
    <a href={`${base}/deals/${id}`} target="_blank" rel="noreferrer" className="font-mono text-accent-text hover:underline" title={`Сделка ${id}`}>
      {id}
    </a>
  );
}

const SBOR: Record<number, string> = { 0: "Сбора нет", 1: "Сбор к прокату", 2: "Сбор к повторке" };

function Sbor({ c, base }: { c: Card; base: string }) {
  const s = c.sbor;
  const line = (v: SborValue) => (
    <>
      <span className="font-medium">{SBOR[v.type] ?? v.type}</span>
      {v.type > 0 && (
        <>
          {" "}
          · {when(v.date)} · сделка <Deal id={v.deal} base={base} />
        </>
      )}
    </>
  );
  return (
    <div
      className={`rounded-[var(--radius-card)] border p-2.5 text-[12.5px] ${s.ok ? "border-accent/30 bg-accent/10" : "border-err/40 bg-err/10"}`}
      data-annot="сбор серии"
    >
      <div>{line(s.crm)}</div>
      <div className="text-[11.5px] text-fg-muted">
        ближайшая бронь <Deal id={s.next_deal.crm} base={base} /> · повторка серии {s.visit.crm ? `#${s.visit.crm}` : "—"}
      </div>
      {!s.ok && (
        <div className="mt-1.5 border-t border-err/30 pt-1.5 text-err">
          <div className="flex items-center gap-1 font-medium">
            <TriangleAlert size={12} aria-hidden /> CRM ещё не пересчитала — по правилам должно быть:
          </div>
          <div className="text-fg">{line(s.calc)}</div>
          <div className="text-[11.5px]">
            ближайшая бронь <Deal id={s.next_deal.calc} base={base} /> · повторка серии {s.visit.calc ? `#${s.visit.calc}` : "—"}
          </div>
        </div>
      )}
    </div>
  );
}

function Reserve({ r, c, base }: { r: SerialReserve; c: Card; base: string }) {
  const percent = r.sum > 0 ? Math.ceil((r.paid / r.sum) * 100) : null;
  const edge = r.cross.length ? "border-l-err bg-err/5" : r.sbor ? "border-l-accent bg-accent/5" : "border-l-transparent";
  return (
    <li className={`flex flex-col gap-1 border-l-4 py-1.5 pl-2 ${edge}`} data-annot={`бронь ${r.deal}`}>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="font-medium tabular-nums">
          {r.start || r.end ? `${when(r.start, false)} – ${when(r.end, false)}` : <span className="text-err">Нет дат</span>}
        </span>
        <span className={`${pill} ${r.stage === "2" ? "bg-ok-bg text-ok" : "border border-border"}`}>{r.stage_name ?? r.stage}</span>
        {r.other_city && <span className={`${pill} bg-warn text-white`}>Сделка в {r.city}</span>}
        {r.is_sale && <span className={`${pill} bg-err text-white`}>Продажа</span>}
        {r.is_select && <span className={`${pill} bg-err text-white`}>На выбор</span>}
        {percent === 0 ? (
          <span className={`${pill} bg-err text-white`}>Оплата 0%</span>
        ) : percent != null && percent <= 10 ? (
          <span className={`${pill} bg-warn text-white`}>Мало оплаты {percent}%</span>
        ) : null}
        {r.visit && (
          <span
            className={`${pill} ${r.visit.needs_serial ? "bg-warn text-white" : "bg-bg-subtle text-fg-muted"}`}
            title={[
              r.visit.needs_serial ? "Нужен данный товар" : "НЕ НУЖЕН товар на повторке",
              r.visit.tailor ? "Нужен мастер" : "",
              r.visit.comment ?? "",
            ]
              .filter(Boolean)
              .join("\n")}
          >
            Повторка {when(r.visit.at)}
          </span>
        )}
        <span className="ml-auto">
          <Deal id={r.deal} base={base} />
        </span>
      </div>
      {r.sbor && (
        <div className="text-[11.5px] text-fg-muted">{c.sbor.crm.type === 2 ? "собираем к повторке этой сделки" : "под эту бронь собираем"}</div>
      )}
      {r.cross.map((d) => (
        <div key={d} className="text-[12px] font-medium text-err">
          Пересечение с договором сделки <Deal id={d} base={base} />
        </div>
      ))}
      {!r.deal_visit.ok && (
        <div className="text-[11.5px] text-err">
          Повторка сделки в CRM {r.deal_visit.crm ? `#${r.deal_visit.crm}` : "не указана"}, по правилам —{" "}
          {r.deal_visit.calc ? `#${r.deal_visit.calc}` : "нет"}
        </div>
      )}
    </li>
  );
}

function Photos({ previews, images }: { previews?: string[] | undefined; images?: string[] | undefined }) {
  if (!previews?.length) return null;
  return (
    <div className="flex flex-wrap gap-1">
      {previews.map((p, i) => (
        <a key={p} href={images?.[i] ?? p} target="_blank" rel="noreferrer">
          <img src={p} alt="" loading="lazy" className="size-12 rounded-[var(--radius-ui)] object-cover" />
        </a>
      ))}
    </div>
  );
}

function Section({ title, annot, children }: { title: string; annot: string; children: ReactNode }) {
  return (
    <section className={block} data-annot={annot}>
      <div className={head}>{title}</div>
      {children}
    </section>
  );
}

/** По каким трекам CRM едет и ехала серия: активные сверху, прошлые свёрнуты. */
function SerialTracks({ serial, base }: { serial: number; base: string }) {
  const q = useQuery({ queryKey: serialTracksKey(serial), queryFn: () => loadSerialTracks(serial) });
  const all = q.data ?? [];
  const now = all.filter((t) => t.active);
  const past = all.filter((t) => !t.active);
  return (
    <Section title="Трекинг" annot="трекинг серии">
      {q.isPending ? (
        <div className="text-fg-muted">Загружаю треки…</div>
      ) : q.isError ? (
        <div className="text-err">Треки не загрузились: {q.error.message}</div>
      ) : !all.length ? (
        <div className="text-fg-muted">Ни к одному треку не привязана.</div>
      ) : (
        <div className="flex flex-col gap-2">
          {now.length ? now.map((t) => <SerialTrack key={t.id} t={t} base={base} />) : <div className="text-fg-muted">Сейчас не в пути.</div>}
          {past.length > 0 && (
            <details>
              <summary className="cursor-pointer text-[12px] text-fg-muted">Прошлые треки: {past.length}</summary>
              <div className="mt-2 flex flex-col gap-2">
                {past.map((t) => (
                  <SerialTrack key={t.id} t={t} base={base} />
                ))}
              </div>
            </details>
          )}
        </div>
      )}
    </Section>
  );
}

function SerialTrack({ t, base }: { t: Track; base: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <TrackLine t={{ ...t, products: [] }} />
      {t.deals.length > 0 && (
        <div className="text-[11.5px] text-fg-faint">
          Сделка{" "}
          {t.deals.map((d, i) => (
            <span key={d}>
              {i > 0 && ", "}
              <Deal id={d} base={base} />
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function Body({ c, base }: { c: Card; base: string }) {
  const p = c.place;
  const { pinned, tasks, recent } = c.comments;
  const pr = c.price;
  return (
    <div className="flex flex-col text-[12.5px]">
      <div className="flex gap-3 pb-3" data-annot="шапка серии">
        {c.photo ? (
          <a href={c.photo} target="_blank" rel="noreferrer" className="flex-none">
            <img src={c.photo} alt="" className="h-[96px] w-[68px] rounded-[var(--radius-ui)] object-cover" />
          </a>
        ) : (
          <div className="grid h-[96px] w-[68px] flex-none place-items-center rounded-[var(--radius-ui)] bg-bg-subtle text-[11px] text-fg-faint">нет фото</div>
        )}
        <div className="flex min-w-0 flex-col gap-0.5">
          <div className="flex items-center gap-2">
            <span className={`font-mono text-[18px] font-semibold ${c.available ? "" : "line-through"}`} title={c.available ? undefined : "Недоступна (available = 0)"}>
              {c.serial}
            </span>
            <a href={`${CRM}#/info?serial=${c.serial}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[12px] text-accent-text hover:underline">
              в CRM <ExternalLink size={11} aria-hidden />
            </a>
          </div>
          <div className="font-medium">{c.name ?? "—"}</div>
          <div className="text-fg-muted">{c.category ?? "—"}</div>
          <div className="text-fg-muted">
            {[c.size || "нет размера", c.color || "нет цвета", c.brand].filter(Boolean).join(" / ")}
          </div>
        </div>
      </div>

      <Section title="Место" annot="место серии">
        <div className="flex flex-wrap items-center gap-x-1.5">
          <MapPin size={13} className="flex-none text-fg-muted" aria-hidden />
          <span className={p.latest === "place" ? "font-semibold" : ""}>{when(p.at)}</span>
          <span>{p.group ?? "—"}</span>
          <span className="font-medium">{p.name ?? "—"}</span>
          <span className="text-fg-muted">{p.city}</span>
          {p.who && <span className="text-fg-muted">· {p.who}</span>}
        </div>
        {p.rfid.map((m) => (
          <div key={m.kind} className="flex flex-wrap items-center gap-x-1.5" title={m.kind === "antenna" ? "RFID-антенна" : "RFID-терминал"}>
            {m.kind === "antenna" ? <Wifi size={13} className="flex-none text-fg-muted" aria-hidden /> : <Smartphone size={13} className="flex-none text-fg-muted" aria-hidden />}
            <span className={p.latest === m.kind ? "font-semibold" : ""}>{when(m.at)}</span>
            <span>{m.name ?? "—"}</span>
            <span className={m.other_city ? "font-medium text-err" : "text-fg-muted"}>{m.city}</span>
          </div>
        ))}
        {p.need_rfid && (
          <div className="flex items-center gap-1.5 text-err">
            <Wifi size={13} aria-hidden /> Нужен RFID
          </div>
        )}
      </Section>

      <Section title="Брони" annot="брони серии">
        <Sbor c={c} base={base} />
        {c.reserves.length ? (
          <ul className="flex flex-col">
            {c.reserves.map((r) => (
              <Reserve key={r.deal} r={r} c={c} base={base} />
            ))}
          </ul>
        ) : (
          <div className="text-fg-muted">Броней нет.</div>
        )}
      </Section>

      <Section title="Повторки с этой серией" annot="повторки серии">
        {c.visits.length ? (
          <ul className="flex flex-col gap-1">
            {c.visits.map((v) => (
              <li key={v.id} className={`flex flex-wrap items-center gap-x-2 ${v.status === 1 ? "" : "text-fg-muted"}`}>
                <span className="tabular-nums">{when(v.at)}</span>
                <span className={`${pill} ${v.status === 1 ? "bg-ok-bg text-ok" : "border border-border"}`}>{v.status_name}</span>
                <span>{v.category}</span>
                {v.tailor && <span className="text-fg-muted">· мастер</span>}
                {v.near && v.status === 1 && <span className="text-fg-muted">· ближайшая у сделки</span>}
                <span className="ml-auto text-[11.5px] text-fg-faint">#{v.id}</span>
                <Deal id={v.deal} base={base} />
              </li>
            ))}
          </ul>
        ) : (
          <div className="text-fg-muted">Повторок с этой серией нет.</div>
        )}
      </Section>

      <Section title="Комментарии и задачи" annot="комментарии серии">
        {pinned && (
          <div>
            <span className="font-semibold">{pinned.who}:</span> <span className="font-semibold text-err">{pinned.text}</span>
            <Photos previews={pinned.previews} images={pinned.images} />
          </div>
        )}
        {tasks.map((t) => (
          <div key={t.id} className={`flex flex-col gap-1 border-l-4 py-1 pl-2 ${t.other_deal ? "border-l-transparent opacity-70" : "border-l-accent bg-accent/5"}`}>
            <div className="flex flex-wrap items-center gap-x-2">
              <span className={`${pill} text-white`} style={{ background: t.color ?? "var(--color-warn)" }} title={t.status_name ?? undefined}>
                {t.name}
              </span>
              <span className="text-[11.5px] text-fg-muted">{t.status_name}</span>
              {t.other_deal && <span className="text-[11.5px] text-fg-muted">не для сделки сбора</span>}
              <span className="ml-auto">
                <Deal id={t.deal} base={base} />
              </span>
            </div>
            {t.text && <div className="whitespace-pre-wrap break-words">{t.text}</div>}
            <Photos previews={t.previews} images={t.images} />
          </div>
        ))}
        {recent.length > 0 && <div className="pt-1 text-[11.5px] text-fg-muted">Недавние (10 дней)</div>}
        {recent.map((x) => (
          <div key={x.id} className="break-words">
            <span className="tabular-nums text-fg-muted">{when(x.at, false)}</span> {x.text}
          </div>
        ))}
        {!pinned && !tasks.length && !recent.length && <div className="text-fg-muted">Открытых задач и свежих комментариев нет.</div>}
      </Section>

      <Section title="Цена" annot="цена серии">
        <div className="flex flex-wrap items-baseline gap-x-3">
          <span className="text-fg-muted">Прокат:</span>
          {pr.rent_status === 2 ? (
            <span className="font-medium text-err">Запрет проката</span>
          ) : pr.rent_status === 3 ? (
            <span className="font-medium text-warn">Неизвестно, уточнить у владельца</span>
          ) : (
            <>
              {pr.rent.map((v, i) =>
                v ? (
                  <span key={i}>
                    <span className="font-semibold tabular-nums">{rub(v)}</span> <span className="text-fg-muted">{["1 день", "2 дня", "3 дня"][i]}</span>
                  </span>
                ) : null,
              )}
              <span>
                <span className="tabular-nums">{rub(pr.rent_min)}</span> <span className="text-fg-muted">миним.</span>
              </span>
            </>
          )}
        </div>
        <div className="flex flex-wrap items-baseline gap-x-3">
          <span className="text-fg-muted">Продажа:</span>
          {pr.sale_status === 2 ? (
            <span className="font-medium text-err">Запрет продажи</span>
          ) : pr.sale_status === 3 ? (
            <span className="font-medium text-warn">Неизвестно, уточнить у владельца</span>
          ) : (
            <>
              <span className="font-semibold tabular-nums">{rub(pr.sale)}</span>
              <span>
                <span className="tabular-nums">{rub(pr.sale_min)}</span> <span className="text-fg-muted">миним.</span>
              </span>
            </>
          )}
        </div>
      </Section>
    </div>
  );
}

/** Окно карточки серии: Esc и клик мимо закрывают, сверху — поле «другая серия» и «обновить». */
export function SerialCardDialog({
  serial,
  base = "",
  onClose,
  onOffer,
}: {
  serial: number;
  base?: string;
  onClose: () => void;
  /** Отмеченные похожие серии — фото с номером в поле ответа; нет (вне чата) — без отметок. */
  onOffer?: OnOffer | undefined;
}) {
  const [current, setCurrent] = useState(serial);
  const [input, setInput] = useState("");
  const q = useQuery(serialCardQuery(current));
  useEffect(() => {
    // defaultPrevented — Esc уже закрыл слайдер фото поверх карточки.
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && !e.defaultPrevented && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const go = () => {
    const n = Number(input.trim());
    if (Number.isInteger(n) && n > 0) {
      setCurrent(n);
      setInput("");
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-3" onClick={onClose}>
      <div
        className="flex max-h-full w-full max-w-[560px] flex-col overflow-hidden rounded-[16px] bg-bg text-[14px] text-fg"
        onClick={(e) => e.stopPropagation()}
        data-annot="карточка серии"
      >
        <div className="flex flex-none items-center gap-2 border-b border-border px-4 py-2.5">
          <div className="min-w-0 flex-1 text-[15px] font-medium">Серия {current}</div>
          <input
            value={input}
            onChange={(e) => setInput(e.target.value.replace(/\D/g, ""))}
            onKeyDown={(e) => e.key === "Enter" && go()}
            placeholder="другая серия"
            inputMode="numeric"
            className="h-8 w-[120px] rounded-[var(--radius-ui)] border border-border bg-bg px-2 text-[13px]"
          />
          <button type="button" onClick={() => void q.refetch()} title="Обновить из CRM" className="grid size-8 place-items-center rounded-full hover:bg-bg-subtle">
            <RefreshCw size={16} className={q.isFetching ? "animate-spin" : ""} />
          </button>
          <button type="button" onClick={onClose} title="Закрыть" className="grid size-8 place-items-center rounded-full hover:bg-bg-subtle">
            <X size={18} />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
          {q.isPending ? (
            <div className="text-[12.5px] text-fg-muted">Загружаю серию из CRM…</div>
          ) : q.isError ? (
            <div className="text-[12.5px] text-err">Не удалось получить серию: {q.error.message}</div>
          ) : (
            <>
              <Body c={q.data} base={base} />
              <SerialTracks serial={current} base={base} />
              <SerialModels key={current} of={{ serial: current }} onOffer={onOffer} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
